import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Button } from "@/components/Button"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Form"
import {
  useComposeSenders,
  useSendComposedEmail,
  type ComposeSenderAddress,
} from "@/hooks/useComposeEmail"
import type { ComposeDraft } from "@/lib/composeDraft"

/**
 * Email → Compose. Send one plain-text email from an allowlisted @playside.co
 * identity, through SES, logged to `email_events`.
 *
 * Deliberately minimal (v1): one recipient, no templates, no merge fields, no
 * bulk. Unlike replying from Gmail (which goes out over Resend and produces no
 * `email_events` row), anything sent here is measurable — it shows up on the Log
 * tab and flips to delivered/bounced when the SES webhook fires.
 *
 * The send is behind a ConfirmDialog: this mails a real person and cannot be
 * recalled, and the form would otherwise fire on an implicit Enter in the To or
 * Subject field. Mirrors the reply flow in CommsThread.
 */
export function EmailCompose() {
  const { state, pathname } = useLocation()
  const navigate = useNavigate()
  // Opened from a response (Comms → Events) with the message already written, or from a
  // plain link with only `to` / `subject` (Welcome offers), or with nothing at all.
  const draft = state as Partial<ComposeDraft> | null
  const { data: sendersData, isLoading: sendersLoading, isError: sendersError } =
    useComposeSenders()
  const send = useSendComposedEmail()

  const [from, setFrom] = useState<ComposeSenderAddress | "">("")
  const [to, setTo] = useState(typeof draft?.to === "string" ? draft.to : "")
  const [subject, setSubject] = useState(typeof draft?.subject === "string" ? draft.subject : "")
  const [body, setBody] = useState(typeof draft?.body === "string" ? draft.body : "")
  // Where this draft came from, shown above the form and cleared once it is sent. The
  // Ordinary drafts keep their template only for the original recipient. Community
  // drafts retain it so changing the recipient cannot bypass outreach checks.
  const [context, setContext] = useState(draft?.context ?? null)
  // Which response this draft started from, and for whom. Held in state, not read from the
  // router, so it is cleared with the form after a send: the now-blank composer must not
  // tag an unrelated next message with the old response and game.
  const [origin, setOrigin] = useState(() => ({
    template: draft?.template,
    templateRef: draft?.templateRef,
    to: typeof draft?.to === "string" ? draft.to.trim().toLowerCase() : null,
  }))
  const [confirmSend, setConfirmSend] = useState(false)

  const senders = sendersData?.senders ?? []
  // Default to the first allowlisted sender once loaded.
  const effectiveFrom = from || senders[0]?.address || ""
  const canSend =
    !!effectiveFrom && to.trim() !== "" && subject.trim() !== "" && body.trim() !== ""

  function doSend() {
    if (!effectiveFrom) return
    const keepsTemplate =
      origin.template &&
      origin.templateRef &&
      origin.to !== null &&
      (origin.template === "join-discord" || to.trim().toLowerCase() === origin.to)
    const template = keepsTemplate ? origin.template : undefined
    const template_ref = keepsTemplate ? origin.templateRef : undefined
    send.mutate(
      { from: effectiveFrom, to: to.trim(), subject: subject.trim(), body, template, template_ref },
      {
        onSuccess: () => {
          // Keep `from` so a run of sends doesn't re-pick the sender each time.
          setTo("")
          setSubject("")
          setBody("")
          setContext(null)
          setOrigin({ template: undefined, templateRef: undefined, to: null })
          // The draft also lives in this history entry's state. Replace it, so Back or a
          // reload remounts a blank composer instead of re-offering the email just sent.
          navigate(pathname, { replace: true, state: null })
        },
      }
    )
  }

  return (
    <div className="space-y-6">
      <p className="text-secondary">
        Send a one-off email from a Playside address. Goes out over SES and is recorded in{" "}
        <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">email_events</code>,
        so delivery is visible on the{" "}
        <Link to="/email/log" className="text-primary hover:underline">
          Log
        </Link>{" "}
        tab — unlike replying from Gmail, which is untracked.
      </p>

      {sendersError && (
        <div className="max-w-2xl rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
          Couldn&apos;t load the sender list, so there&apos;s nothing to send from. Reload the page;
          if it keeps failing your admin session may have expired.
        </div>
      )}

      {context && (
        <div className="max-w-2xl rounded-md border border-border bg-surface px-4 py-3 text-sm">
          <p className="text-primary">
            Started from <span className="font-medium">{context.who}</span> ·{" "}
            {context.responseName}
          </p>
          <p className="mt-1 break-all font-mono text-xs text-secondary">{context.link}</p>
          <p className="mt-1 text-xs text-secondary">{context.linkNote}</p>
          {context.handledAt && (
            <p className="mt-2 text-xs text-amber-400">
              You already sent this response on{" "}
              {new Date(context.handledAt).toLocaleDateString([], { month: "short", day: "numeric" })}.
              {origin.template === "join-discord" ? "This community invitation cannot be sent again." : "Sending again is a follow-up."}
            </p>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (canSend && !send.isPending) setConfirmSend(true)
        }}
        className="max-w-2xl space-y-4"
      >
        <div className="space-y-2">
          <label htmlFor="compose-from" className="text-sm font-medium text-primary">
            From
          </label>
          <Select
            value={effectiveFrom}
            onValueChange={(v) => setFrom(v as ComposeSenderAddress)}
            disabled={sendersLoading || senders.length === 0}
          >
            <SelectTrigger id="compose-from">
              <SelectValue placeholder="Select a sender" />
            </SelectTrigger>
            <SelectContent>
              {senders.map((s) => (
                <SelectItem key={s.address} value={s.address}>
                  {s.name} &lt;{s.address}&gt;
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Input
          id="compose-to"
          label="To"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="coach@example.com"
        />

        <Input
          id="compose-subject"
          label="Subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
        />

        <div className="space-y-2">
          <label htmlFor="compose-body" className="text-sm font-medium text-primary">
            Message
          </label>
          <textarea
            id="compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            placeholder="Plain text. Sent as-is — no template, no branding."
            className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
          <p className="text-xs text-secondary">
            Sent as plain text so it reads like a personal note, which also delivers best. If
            you&apos;re offering something commercial, include a line telling them how to opt out.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!canSend || send.isPending}>
            {send.isPending ? "Sending…" : "Send"}
          </Button>

          {send.isSuccess && (
            <span className="text-sm text-emerald-400">
              Sent. It should appear on the Log tab shortly.
            </span>
          )}
          {send.isError && (
            <span className="text-sm text-rose-400">
              {/* APIError.message is the generic "API Error 502: CODE" — the
                  backend's actual reason lives on originalError. */}
              {send.error?.originalError?.message || "Send failed."}
            </span>
          )}
        </div>
      </form>

      <div className="max-w-2xl rounded-lg border border-border bg-surface p-4 text-sm text-secondary">
        <p className="font-medium text-primary">{origin.template === "join-discord" ? "Community invitation" : "One at a time, on purpose"}</p>
        {origin.template === "join-discord" ? <p className="mt-1">
          One invitation per recipient, with a shared limit of 20 per 24 hours. An unsubscribe
          link is included when sent. Community outreach requires known coach or admin status;
          player accounts are excluded.
        </p> : <p className="mt-1">
          Addresses that have already hard-bounced or reported us as spam are refused — that check
          is server-side. Everything else is on you: there&apos;s no unsubscribe handling and no
          rate limiting here. For the same message to many people — a trial offer to signups, say —
          use the re-engagement path instead: it already has send caps, a per-recipient lifetime
          cap, and a signed one-click unsubscribe. Blasting a list from here is how complaints land
          on the domain that carries our login codes.
        </p>}
      </div>

      <ConfirmDialog
        open={confirmSend}
        onOpenChange={setConfirmSend}
        title="Send this email?"
        description={`This sends a real email from ${effectiveFrom} to ${to.trim() || "the recipient"}. It cannot be recalled.`}
        confirmLabel="Send email"
        onConfirm={doSend}
      />
    </div>
  )
}
